import { useTranslation } from "react-i18next";
import * as PriceUtils from "~/utils/PriceUtils";
import { Button, CardContent, Checkbox, styled, Typography } from "~/common/mui";
import Tag from "~/common/components/Tag/Tag";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { Param } from "../common/components/v2/Params";
import type { OrderData } from "~/domains/orders/types/modelsV2";
import type { DocumentListRow } from "~/features/orders/orderList/useOrderListState";
import { C_WHITE } from "~/common/components/icon/Colors";
import { getOrderTagVariant, isErrorEntry } from "../common/utils/util";

interface Props {
  row: DocumentListRow<OrderData>;
  selectable?: boolean;
  isAllSelected?: boolean;
  onToggleSelectAll?: (newValue: boolean) => void;
  disabled?: boolean;
  sticky?: boolean;
}

export const OrderCardHeader = ({ row, selectable, isAllSelected, onToggleSelectAll, disabled, sticky }: Props) => {
  const { t } = useTranslation();

  const handleSelectAll = () => {
    if (!selectable || disabled) {
      return;
    }
    onToggleSelectAll?.(!isAllSelected);
  };

  return (
    <DetailsSummary sticky={sticky}>
      <Checkbox
        checked={!!isAllSelected}
        disabled={disabled || !selectable}
        sx={!selectable ? { visibility: "hidden" } : undefined}
        onChange={handleSelectAll}
        data-test-id={"order-card-select-all"}
      />
      <Param label={t("orderListPage.table.header.soNumber")} value={row.data.soNumber} />
      <Param label={t("orderListPage.table.header.poNumber")} value={row.data.poNumber} />
      <Param
        label={t("orderListPage.order.date.text")}
        value={formatNullableDate(row.data.datePlaced, DateFormatType.DATE)}
      />
      <Param
        label={t("orderListPage.table.header.totalOnly")}
        value={
          <>
            <Typography variant="bodyLarge" component="b" color="textPrimary">
              {PriceUtils.text(row.data.totalPrice)}
            </Typography>
            {row.data.hasSurcharge && "*"}
          </>
        }
      />
      <Param
        label={t("orderListPage.table.header.statusOnly")}
        value={<Tag variant={getOrderTagVariant(row.data.orderStatus)}>{t(row.data.orderStatus)}</Tag>}
      />
      <Button variant="text" href={row.data.detailsUrl} disabled={isErrorEntry(row)}>
        {t("orderListPage.view.details.text")}
      </Button>
    </DetailsSummary>
  );
};

const DetailsSummary = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== "sticky",
})<{ sticky?: boolean }>(({ theme, sticky }) => ({
  alignItems: "center",
  padding: "16px",
  display: "grid",
  columnGap: "16px",
  gridTemplateAreas: "checkbox so po date total status cta",
  gridTemplateColumns: "auto repeat(5, minmax(0, 1fr)) auto",
  position: sticky ? "sticky" : "static",
  top: sticky ? 0 : "auto",
  zIndex: sticky ? 5 : "auto",
  background: sticky ? C_WHITE : "transparent",
  borderTopLeftRadius: "12px",
  borderTopRightRadius: "12px",
  borderBottom: `1px solid ${theme.palette.divider}`,
}));
